import {
  buildTextIndex,
  findAllMatches,
  type TextIndex,
} from "@/components/common/PdfViewer/highlights"

/** Why a selection could not become an anchor. Callers treat every one the
 *  same way — no chip is raised (the quiet contract of a selection UI) — but
 *  the codes stay distinct so tests can pin WHICH rule refused. */
export type EvidencePickError =
  | "empty"
  | "outside"
  | "cross-page"
  | "unfindable"
  | "too-long"

export interface EvidencePick {
  quote: string
  page: number
  /** nth copy of the quote on the page, 1-based — omitted means the first. */
  occurrence?: number
}

/** Matches the backend's `CreateStatementEvidenceIn.quote` cap. */
const MAX_QUOTE_LENGTH = 4000

/**
 * Turn the browser's CURRENT text selection into a `{quote, page, occurrence}`
 * evidence anchor — the picker behind "Fundstelle im Dokument markieren"
 * (TUR-1445).
 *
 * The contract is the viewer's own: an anchor is accepted ONLY when
 * `findAllMatches` (the same fold- and whitespace-flexible matcher the jump
 * effect uses via `findQuote`) can re-find the captured string in the page's
 * text index — which guarantees the anchor will highlight after creation.
 * `selection.toString()` from a pdf.js text layer is NOT the page's logical
 * text (missing spaces at line wraps, ligatures), which is exactly what that
 * matcher tolerates.
 *
 * SINGLE-PAGE BY CONTRACT: `EvidenceAnchor` carries one `page`, and
 * `contentVisibility: auto` on far-off pages makes a cross-page drag
 * unreliable anyway — so a selection spanning two pages is refused, named.
 *
 * `occurrence` is derived from WHERE the selection sits among the page's
 * matches of its own text, so selecting the second copy of a repeated line
 * anchors the second copy — the same 1-based coordinate the pipeline's
 * anchors carry.
 */
export function captureEvidenceSelection(
  root: HTMLElement,
): EvidencePick | EvidencePickError {
  const selection = window.getSelection()
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
    return "empty"
  }
  const quote = selection.toString().trim()
  if (!quote) return "empty"
  if (quote.length > MAX_QUOTE_LENGTH) return "too-long"

  const startPage = pageElementOf(selection.anchorNode)
  const endPage = pageElementOf(selection.focusNode)
  if (!startPage || !root.contains(startPage)) return "outside"
  if (endPage !== startPage) return "cross-page"

  const page = Number(startPage.getAttribute("data-page-number"))
  if (!Number.isFinite(page) || page < 1) return "outside"

  // UNCACHED, per call: the viewer next door keeps a per-page index cache
  // (`DeclarationDocumentViewer.tsx:544-552`, invalidated on
  // `onPageReady`), but it is private to that component and nothing exposes
  // it here. `DocumentPane` therefore guards this function instead — it only
  // calls it for a live selection whose text actually changed. Plumbing the
  // viewer's cache out to this module is the real fix and is still open.
  const index = buildTextIndex(startPage)
  const matches = findAllMatches(index.text, quote)
  if (matches.length === 0) return "unfindable"

  const range = selection.getRangeAt(0)
  const startAbs = absoluteOffset(
    index,
    range.startContainer,
    range.startOffset,
  )
  let occurrence = 1
  if (startAbs !== null) {
    // The match the selection actually sits in (small tolerance for the fold's
    // whitespace drift); an unlocatable start degrades to the first copy, the
    // same rule `findQuote` itself follows.
    const hit = matches.findIndex(
      (m) => startAbs >= m.start - 2 && startAbs <= m.end,
    )
    if (hit > 0) occurrence = hit + 1
  }

  return {
    quote,
    page,
    ...(occurrence > 1 ? { occurrence } : {}),
  }
}

/**
 * Cut a selection that ran out of the viewer back to the page it STARTED on,
 * rewriting the live selection in place. Returns whether it changed anything.
 *
 * Live-verified (dense annex table, 15-page declaration): once the pointer
 * leaves the document pane, the browser keeps extending the selection across
 * the app's own DOM, so a drag off the pane arrived here as 4,404 / 26,356 /
 * 4,188 characters of mixed PDF and app UI. Every one of those was refused —
 * and note WHICH refusal: the `MAX_QUOTE_LENGTH` check above runs BEFORE the
 * page checks, so all three came back "too-long", not "cross-page". A clamp
 * applied at the page checks would therefore never have run for any of them,
 * which is why this is a separate step the caller runs FIRST, before the
 * capture measures anything.
 *
 * Rewriting the live selection (rather than capturing from a private range) is
 * deliberate: the reviewer SEES what was kept, and the app-wide blue smear
 * over the left pane disappears at the same moment the chip appears.
 *
 * NARROW BY DESIGN, and the caller narrows it further. This fires only when
 * the focus node has left the viewer root entirely, and the caller only runs
 * it for a completed pointer gesture (never a keyup). Everything else is a
 * deliberate reader gesture and is left exactly as the reviewer made it:
 * a selection inside one page, a selection across two PDF pages (ordinary
 * copying — the single-page rule belongs to the evidence anchor, not to the
 * document), a keyboard extension, and any gesture that did not start on a
 * page in this viewer. Those still reach the capture and are refused there
 * on their merits, unmutated.
 */
export function clampSelectionToAnchorPage(root: HTMLElement): boolean {
  const selection = window.getSelection()
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
    return false
  }
  const anchorNode = selection.anchorNode
  const anchorOffset = selection.anchorOffset
  const anchorPage = pageElementOf(anchorNode)
  if (!anchorNode || !anchorPage || !root.contains(anchorPage)) return false
  // ONLY a selection whose far end escaped the viewer entirely. A focus still
  // inside the viewer — including on ANOTHER PDF page — is a deliberate
  // reader gesture: selecting across a page break to copy text is ordinary
  // document behaviour, and the single-page rule belongs to the evidence
  // anchor, not to the document. Those are left untouched, and the capture
  // refuses them exactly as before without mutating the reviewer's selection.
  // Cheap, and it MUST stay above `buildTextIndex`: this runs on every
  // completed pointer gesture, ahead of the caller's unchanged-selection memo.
  const focusNode = selection.focusNode
  if (!focusNode) return false
  // FAIL SAFE, and deliberately independent of `root`. Rewriting a selection
  // the reviewer can still see is the worst thing this function can do, so
  // "the focus left the viewer" has to be PROVEN, not inferred from one
  // ancestor test: if `root` ever resolves to an element that does not host
  // the text layers, every ordinary in-pane drag would look like an exit and
  // be rewritten. A focus sitting on ANY page — this one, another one, any
  // viewer — is decided here without consulting `root` at all.
  if (pageElementOf(focusNode)) return false
  // ...and anywhere else inside the pane body is equally not an exit.
  if (root.contains(focusNode)) return false

  // Endpoints come from the TEXT LAYER, never the page element at large, so a
  // clamp can only ever land on document text — not on viewer chrome such as
  // the "SEITE n" divider label, a mark box, or a quote overlay. Falls back to
  // the page itself for a page whose text layer has not rendered yet.
  const textLayer =
    anchorPage.querySelector<HTMLElement>(
      ".textLayer, .react-pdf__Page__textContent",
    ) ?? anchorPage
  // Only nodes carrying REAL text. pdf.js appends its own furniture to a text
  // layer (an `endOfContent` div, a `<br>` per soft wrap), and clamping onto
  // one would end the selection on whitespace: the live selection and the
  // trimmed quote the chip promises would then be different strings, which is
  // the one thing the chip must never do.
  const nodes = buildTextIndex(textLayer).nodes.filter(
    (n) => (n.node.nodeValue ?? "").trim().length > 0,
  )
  if (nodes.length === 0) return false

  const range = selection.getRangeAt(0)
  // A Range is always in document order, so the anchor sitting at its start
  // means the reviewer dragged forwards; otherwise they dragged backwards and
  // the anchor is the range's END.
  const forwards =
    range.startContainer === anchorNode && range.startOffset === anchorOffset

  const edge = forwards ? nodes[nodes.length - 1] : nodes[0]
  const extentNode = edge.node
  // Trailing whitespace inside the edge node itself is excluded too, for the
  // same reason: the selection must end where the text does.
  const raw = extentNode.nodeValue ?? ""
  const extentOffset = forwards
    ? raw.trimEnd().length
    : raw.length - raw.trimStart().length

  // EVERY range operation below can throw on exactly the states this function
  // exists for: a stale offset into a re-rendered text layer raises
  // IndexSizeError, a detached node WrongDocumentError. The listeners live on
  // `document`, so an escaping throw would kill the handler for every mouseup
  // in the app whose anchor sits in this pane, not just for the gesture that
  // hit the bad state. Refusing the clamp is always a safe answer: the caller
  // then reads the selection back and decides on it unmodified.
  try {
    // A drag that began at the very end (or very start) of the page keeps
    // nothing; leave the caller's existing refusal to speak rather than
    // replacing a real selection with an empty one. Probed in document order,
    // since a Range cannot express the backwards case.
    const probe = document.createRange()
    if (forwards) {
      probe.setStart(anchorNode, anchorOffset)
      probe.setEnd(extentNode, extentOffset)
    } else {
      probe.setStart(extentNode, extentOffset)
      probe.setEnd(anchorNode, anchorOffset)
    }
    if (probe.collapsed) return false

    // `setBaseAndExtent`, never `removeAllRanges` + `addRange`: a Range
    // carries no direction, so re-adding one turns a backwards selection
    // forwards (an 8 -> 0 anchor/focus comes back as 0 -> 8) and the
    // reviewer's next Shift+Arrow then moves the wrong end. The base stays
    // exactly where they pressed down; only the far end is pulled onto the
    // page.
    selection.setBaseAndExtent(
      anchorNode,
      anchorOffset,
      extentNode,
      extentOffset,
    )
  } catch {
    return false
  }

  // VERIFY the browser actually took the rewrite. A live exit below the pane
  // produced a chip standing over a selection that had length 0, and a
  // rewrite can fail to stick for reasons this module cannot see from here:
  // `content-visibility: auto` skips far-off pages, so boundary points inside
  // them are not always selectable, and a text layer re-rendered between the
  // drag and the rewrite leaves detached nodes behind. Reporting false lets
  // the caller read the selection back, find nothing, and raise no chip —
  // rather than promising a pick with no visible selection behind it.
  if (selection.isCollapsed || !selection.toString()) return false
  return true
}

/** The react-pdf page wrapper the node sits in — react-pdf stamps
 *  `data-page-number` on every `.react-pdf__Page`. */
export function pageElementOf(node: Node | null): HTMLElement | null {
  if (!node) return null
  const el = node instanceof Element ? node : node.parentElement
  return (el?.closest("[data-page-number]") as HTMLElement | null) ?? null
}

/** A (text node, offset) position as an absolute offset into the page's text
 *  index, or null when the container is not one of the index's text nodes. */
function absoluteOffset(
  index: TextIndex,
  container: Node,
  offset: number,
): number | null {
  for (const entry of index.nodes) {
    if (entry.node === container) return entry.start + offset
  }
  return null
}
